import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { scheduleOnce } from '@ember/runloop';
import { dasherize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { call } from '@nullvoxpopuli/ember-composable-helpers';
import { eq, or } from 'ember-truth-helpers';
import random from 'lodash/random';
import { dedupeTracked, localCopy } from 'tracked-toolbox';

function id(parentId: string, child: ComboBoxOption): string {
  if (typeof child === 'object') {
    return `${parentId}-${child.id}`;
  } else {
    return `${parentId}-${dasherize(child)}`;
  }
}

function optionId(elementId: string, option: ComboBoxOption): string {
  if (typeof option === 'object') {
    return `${elementId}-${option.id}`;
  } else {
    return `${elementId}-${dasherize(option)}`;
  }
}

export type ComboBoxOption =
  | string
  | {
      id: string;
    };

interface ComboBoxSignature<O> {
  Args: {
    activeOptionClass?: string;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    closestOption?: string | null;
    emptyOption?: string;
    inputClass?: string;
    inputId?: string;
    invalidInputClass?: string;
    invalidLabelClass?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    isReadonly?: boolean;
    listboxClass?: string;
    onInput?: (value: string) => string | undefined;
    onFocusOut?: () => void;
    onSelect?: (value: O) => void;
    options?: O[] | null;
    optionClass?: string;
    placeholder?: string;
    required?: boolean;
    scrollToSelection?: boolean;
    searchField?: keyof O;
    selected?: O | null;
    selectedOptionClass?: string;
    showDropdownArrow?: boolean;
    shouldDisableOption?: (option: O) => boolean;
    type?: string;
    validateUserInput?: (userInput: string, selectedValue: string) => boolean;
  };
  Element: HTMLDivElement;
  Blocks: {
    default: O[];
  };
}

export default class ComboBox<O = ComboBoxOption> extends Component<ComboBoxSignature<O>> {
  @tracked declare element: HTMLElement;
  @tracked isUserInputValid = true;
  @tracked userInput: string | null = null;

  @dedupeTracked shouldShowListbox = false;
  @dedupeTracked activedescendant: string | null = null;

  @localCopy('args.emptyOption', 'no options') emptyOption!: string;
  @localCopy('args.scrollToSelection', true) scrollToSelection!: boolean;
  @localCopy('args.type', 'text') type!: string;

  @action
  registerElement(element: HTMLElement): void {
    this.element = element;
  }

  // tracks the id of the currently focused option
  get selectedValue(): string {
    return this.args.searchField && this.args.selected && typeof this.args.selected === 'object'
      ? <string>this.args.selected[this.args.searchField]
      : <string>this.args.selected || '';
  }

  get value(): string | null {
    if (!this.args.selected || !this.isUserInputValid) {
      return this.userInput;
    } else {
      return this.selectedValue;
    }
  }

  get optionIds(): string[] {
    return (this.args.options || []).map((option) => id(this.element.id, <ComboBoxOption>option));
  }

  get selectedId(): string | null {
    if (this.args.selected) {
      return id(this.element.id, <ComboBoxOption>this.args.selected);
    }
    return null;
  }

  get closestOptionId(): string | null {
    if (this.args.closestOption) {
      return id(this.element.id, this.args.closestOption);
    }
    return null;
  }

  @action
  focusIn(): void {
    this._handleFocusIn();
  }

  @action
  focusOut(event: FocusEvent): void {
    const { relatedTarget } = event;
    // focusOut fires when focus is moved to the options
    // only handle a focusOut if focus is actual leaving the component
    if (!this.element.contains(<Node>relatedTarget)) {
      this.args.onFocusOut?.();
      this._handleFocusOut();
    }
  }

  @action
  keyUp(e: KeyboardEvent): void {
    // handle keyboard events. allow user to navigate combobox according to WAI-ARIA Combobox spec
    switch (e.key) {
      case 'Up': // IE/Edge specific value
      case 'ArrowUp':
        this._handleArrowUp();
        e.stopPropagation();
        e.preventDefault();
        break;

      case 'Down': // IE/Edge specific value
      case 'ArrowDown':
        this._handleArrowDown();
        e.stopPropagation();
        e.preventDefault();
        break;

      case 'Enter':
        this._handleEnter();
        e.stopPropagation();
        e.preventDefault();
        break;

      case 'Esc': // IE/Edge specific value
      case 'Escape':
        this._handleEscape();
        e.stopPropagation();
        e.preventDefault();
        break;
    }
  }

  showListBox(): void {
    const { isDisabled, isReadonly } = this.args;

    this.shouldShowListbox = !(isDisabled || isReadonly);
  }

  _handleArrowUp(): void {
    //    select the previous selection
    // OR return focus to the input if there is no previous selection
    const input = <HTMLElement>this.element.querySelector('[role="combobox"]');
    let activeOptionId = null;
    const activeOptionIndex = this.activedescendant ? this.optionIds.indexOf(this.activedescendant) : -1;
    if (activeOptionIndex === 0) {
      input?.focus();
    } else if (activeOptionIndex !== -1) {
      activeOptionId = this.optionIds[activeOptionIndex - 1];
    } else if (this.closestOptionId) {
      activeOptionId = this.closestOptionId;
    }
    if (activeOptionId) {
      const activeOptionEl = document.getElementById(activeOptionId);
      activeOptionEl?.focus();
      this.activedescendant = activeOptionId;
    }
  }

  _handleArrowDown(): void {
    //    noop if at bottom of list and has active option
    // OR focus the next option if has active option
    // OR focus the selected option if no active option and has selection
    // OR focus the first option if no active option and no selection
    let activeOptionId: string | undefined;
    if (!this.shouldShowListbox) {
      this.showListBox();
      this._scrollToSelection();
    }
    if (this.activedescendant) {
      const activeOptionIndex = this.optionIds.indexOf(this.activedescendant);
      if (activeOptionIndex !== this.optionIds.length - 1) {
        activeOptionId = this.optionIds[activeOptionIndex + 1];
      } else {
        activeOptionId = this.activedescendant;
      }
    } else if (this.args.selected) {
      const selectedIndex = this.selectedId ? this.optionIds.indexOf(this.selectedId) : -1;
      // the selected item may not exist in the options
      // if it doesn't use the closest option, otherwise use the first option
      if (selectedIndex !== -1) {
        activeOptionId = this.optionIds[selectedIndex];
      } else if (this.closestOptionId) {
        activeOptionId = this.closestOptionId;
      } else {
        activeOptionId = this.optionIds[0];
      }
    } else {
      activeOptionId = this.optionIds[0];
    }
    if (activeOptionId) {
      const activeOptionEl = document.getElementById(activeOptionId);
      activeOptionEl?.focus();
      this.activedescendant = activeOptionId;
    }
  }

  _handleEnter(): void {
    //    select the focused suggestion
    // OR select the first suggestion as a default
    const activeOptionEl = document.getElementById(this.activedescendant || this.optionIds[0]!);
    activeOptionEl?.click();
  }

  _handleEscape(): void {
    // clear the userInput and suggestions, and return focus to input
    const input = <HTMLElement>this.element.querySelector('[role="combobox"]');
    this.userInput = '';
    this.activedescendant = null;
    input?.focus();
    this.shouldShowListbox = false; // hide the listbox after focusIn fires
  }

  _handleFocusIn(): void {
    if (!this.shouldShowListbox) {
      this.showListBox();
      this._scrollToSelection();
    }
  }

  _handleFocusOut(): void {
    if (!this.isUserInputValid) {
      // prevent the input being left in an invalid state
      this.userInput = this.selectedValue;
      this.isUserInputValid = true;
    }
    this.shouldShowListbox = false;
    this.activedescendant = null;
  }

  _scrollToSelection(): void {
    let scrollToId: string | null = null;
    if (this.selectedId && this.optionIds.includes(this.selectedId)) {
      scrollToId = this.selectedId;
    } else if (this.closestOptionId && this.optionIds.includes(this.closestOptionId)) {
      scrollToId = this.closestOptionId;
    }
    if (this.scrollToSelection && scrollToId) {
      // schedule afterRender to make sure the dropdown options are rendered
      /* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions */

      // ember-lifeline explicitly doesn't allow scheduleTask on 'afterRender'
      // eslint-disable-next-line ember/no-runloop
      scheduleOnce('afterRender', this, function () {
        if (this.isDestroying || this.isDestroyed) return;
        const el = document.getElementById(scrollToId);
        el?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'start' });
      });
      /* eslint-enable ember/no-incorrect-calls-with-inline-anonymous-functions */
    }
  }

  @action
  didType(event: Event): void {
    this.showListBox();
    let userInput = (<HTMLInputElement>event.target!).value;
    this.userInput = userInput;
    userInput = this.args.onInput?.(userInput) ?? userInput;
    const isUserInputValid = this.args.validateUserInput?.(userInput, this.selectedValue) ?? true;
    this.isUserInputValid = isUserInputValid;
    if (isUserInputValid) {
      this.args.onSelect?.(<O>userInput);
    }
  }

  @action
  shouldDisableOption(option: O): boolean {
    return this.args.shouldDisableOption?.(option) ?? false;
  }

  @action
  didSelect(option: O): void {
    if (!this.shouldDisableOption(option)) {
      const input = <HTMLElement>this.element.querySelector('[role="combobox"]');
      this.args.onSelect?.(option);
      this.isUserInputValid = true;
      this.activedescendant = null;
      input?.focus();
      this.shouldShowListbox = false; // hide the listbox after focusIn fires
    }
  }

  @action
  didClickInput(): void {
    this.showListBox();
    this._scrollToSelection();
  }

  optionId(elementId: string, option: O): string {
    return optionId(elementId, <ComboBoxOption>option);
  }

  <template>
    <div
      id="combobox-{{random 1 1797}}"
      {{didInsert this.registerElement}}
      {{on "focusin" this.focusIn}}
      {{on "focusout" this.focusOut}}
      {{on "keyup" this.keyUp}}
      ...attributes
    >
      <div class="flex relative">
        <input
          autocomplete="off"
          aria-activedescendant={{or this.activedescendant ""}}
          aria-autocomplete="list"
          aria-controls={{concat this.element.id "listbox"}}
          aria-expanded={{if this.shouldShowListbox "true" "false"}}
          aria-invalid={{if this.isUserInputValid "false" "true"}}
          aria-labelledby={{@ariaLabelledBy}}
          aria-owns={{concat this.element.id "listbox"}}
          aria-required={{if @required "true" "false"}}
          class="{{@inputClass}} {{unless this.isUserInputValid @invalidInputClass}} {{if @isReadonly ' readonly'}}"
          disabled={{@isDisabled}}
          id={{@inputId}}
          {{on "input" this.didType}}
          {{on "click" this.didClickInput}}
          placeholder={{@placeholder}}
          readonly={{@isReadonly}}
          required={{@required}}
          role="combobox"
          spellcheck="false"
          type={{this.type}}
          value={{if @isLoading "Loading..." this.value}}
        />
        {{#if @showDropdownArrow}}
          <img
            class="pl-2 absolute right-[10px] top-4"
            src="/assets/images/chevron-down-gray.svg"
            alt="down arrow, inactive"
          />
        {{/if}}
      </div>
      <div
        class={{if this.shouldShowListbox @listboxClass "hidden"}}
        id={{concat this.element.id "listbox"}}
        role="listbox"
      >
        {{#if this.shouldShowListbox}}
          {{#each @options as |listboxOption|}}
            {{#let (this.optionId this.element.id listboxOption) as |optionId|}}
              <div
                class={{concat
                  @optionClass
                  " "
                  (if (eq this.activedescendant optionId) @activeOptionClass)
                  " "
                  (if (eq this.selectedId optionId) @selectedOptionClass)
                  " "
                  (if (call (fn this.shouldDisableOption listboxOption)) "cursor-text")
                }}
                role="option"
                tabindex="-1"
                id={{optionId}}
                aria-selected={{if (eq optionId this.selectedId) "true" "false"}}
                data-test-listbox-option
                {{on "click" (fn this.didSelect listboxOption)}}
              >
                {{yield listboxOption}}
              </div>
            {{/let}}
          {{else}}{{#if this.emptyOption}}
              {{this.emptyOption}}
            {{/if}}{{/each}}
        {{/if}}
      </div>
    </div>
  </template>
}
